import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { Backdrop, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Button } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";
import ReplayIcon from "@mui/icons-material/Replay";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import careiqapi from "../api/careiqapi";
import ToastMessage from "../utils/mui/righttoptoast";
import { getPracticeId } from "../config/practice";

const DEPT_NAMES = { 1: "Odessa", 2: "Frisco" };

const STATUS_META = {
  1: { label: "New", cls: "text-[#1E40AF] bg-[#DBEAFE] border border-[#93C5FD]" },
  2: { label: "Handled", cls: "text-[#166534] bg-[#F0FDF4] border border-[#86EFAC]" },
  3: { label: "Needs Callback", cls: "text-[#B45309] bg-[#FFFBEB] border border-[#FCD34D]" },
};

const FILTER_CHIPS = [
  { value: 1, label: "New" },
  { value: 3, label: "Needs Callback" },
  { value: 2, label: "Handled" },
  { value: 0, label: "All" },
];

/**
 * Leads submitted on the practice's public website (high-bp.com).
 * type="appointment"  -> Communication > Appt Requests (existing patients)
 * type="new_patient"  -> Onboarding > Web Requests (adds "Send intake link")
 * The list endpoint is department-scoped server-side, same partition as
 * the Onboarding queue, so Frisco staff only see Frisco leads.
 */
const WebRequestsTable = ({ type }) => {
  const practiseid = getPracticeId();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Default to All (0): backfilled history is status=Handled and would be
  // invisible behind the New chip; counts on the chips guide daily triage.
  const [statusFilter, setStatusFilter] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [notesOpenId, setNotesOpenId] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [confirmSend, setConfirmSend] = useState(null); // row pending intake-link SMS

  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [openToast, setOpenToast] = useState(false);
  const toast = (msg, severity = "success") => {
    setToastMessage(msg);
    setToastSeverity(severity);
    setOpenToast(true);
  };

  const fetchRows = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await careiqapi.get(
        `/v1/${practiseid}/website-requests/get?request_type=${type}`,
      );
      if (response.status === 200) setRows(response.data || []);
      else setRows([]);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const filteredRows = useMemo(() => {
    let data = [...rows];
    if (statusFilter) data = data.filter((r) => r.status === statusFilter);
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      data = data.filter(
        (r) =>
          `${r.firstname} ${r.lastname}`.toLowerCase().includes(term) ||
          (r.phone || "").toLowerCase().includes(term) ||
          (r.email || "").toLowerCase().includes(term) ||
          (r.reason || "").toLowerCase().includes(term),
      );
    }
    return data;
  }, [rows, statusFilter, searchTerm]);

  const chipCounts = useMemo(() => {
    const counts = { 0: rows.length, 1: 0, 2: 0, 3: 0 };
    rows.forEach((r) => {
      counts[r.status] = (counts[r.status] || 0) + 1;
    });
    return counts;
  }, [rows]);

  const updateStatus = async (row, status) => {
    try {
      setBusy(true);
      const response = await careiqapi.put(
        `/v1/${practiseid}/website-requests/${row.id}/update-status`,
        { status },
      );
      if (response.status === 200) {
        toast(`Marked as ${STATUS_META[status].label}`);
        fetchRows(true);
      } else toast(response.message || "Update failed", "error");
    } catch {
      toast("Update failed", "error");
    } finally {
      setBusy(false);
    }
  };

  const saveNotes = async (row) => {
    try {
      setBusy(true);
      const response = await careiqapi.put(
        `/v1/${practiseid}/website-requests/${row.id}/update-notes`,
        { admin_notes: notesDraft },
      );
      if (response.status === 200) {
        toast("Notes saved");
        setNotesOpenId(null);
        fetchRows(true);
      } else toast(response.message || "Save failed", "error");
    } catch {
      toast("Save failed", "error");
    } finally {
      setBusy(false);
    }
  };

  const sendIntakeLink = async (row) => {
    try {
      setBusy(true);
      const response = await careiqapi.post(
        `/v1/${practiseid}/website-requests/${row.id}/send-intake-link`,
      );
      if (response.status === 200) {
        toast(`Intake link texted to ${row.phone}`);
        fetchRows(true);
      } else toast(response.message || "Send failed", "error");
    } catch (e) {
      toast(e?.response?.data?.message || "Send failed", "error");
    } finally {
      setBusy(false);
      setConfirmSend(null);
    }
  };

  const toggleNotes = (row) => {
    if (notesOpenId === row.id) {
      setNotesOpenId(null);
    } else {
      setNotesOpenId(row.id);
      setNotesDraft(row.admin_notes || "");
    }
  };

  return (
    <div className="w-full rounded-[20px] bg-[#FFF] p-4">
      {/* Toolbar */}
      <div className="w-full flex flex-wrap gap-3 justify-between items-center mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          {FILTER_CHIPS.map((chip) => (
            <button
              key={chip.value}
              onClick={() => setStatusFilter(chip.value)}
              className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                statusFilter === chip.value
                  ? "bg-[#1D2951] text-white border-[#1D2951]"
                  : "bg-white text-[#1d2951] border-[#D5D5D5] hover:bg-[#F5F6FA]"
              }`}
            >
              {chip.label} ({chipCounts[chip.value] || 0})
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-[#F5F6FA] border border-[#D5D5D5] rounded-[15px]">
            <FontAwesomeIcon icon={faSearch} className="text-gray-400 ml-3" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-[#F5F6FA] py-1 pl-2 pr-3 text-sm outline-none rounded-[15px] w-[220px]"
              placeholder="Search name, phone, reason"
            />
          </div>
          <button
            onClick={() => fetchRows()}
            title="Refresh"
            className="text-[#667085] hover:text-[#1d2951]"
          >
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-t-2xl shadow-md max-h-[62vh] overflow-y-auto">
        <table className="min-w-full table-auto border-collapse">
          <thead>
            <tr
              className="text-left text-sm sticky top-0 z-10"
              style={{ borderBottom: "1px solid #E4E4E7", background: "#1D2951" }}
            >
              <th className="py-3 px-4 font-medium text-white whitespace-nowrap">Received</th>
              <th className="py-3 px-4 font-medium text-white whitespace-nowrap">Patient</th>
              <th className="py-3 px-4 font-medium text-white whitespace-nowrap">Phone</th>
              <th className="py-3 px-4 font-medium text-white whitespace-nowrap">Location</th>
              <th className="py-3 px-4 font-medium text-white whitespace-nowrap">Requested Slot</th>
              <th className="py-3 px-4 font-medium text-white whitespace-nowrap">Provider</th>
              <th className="py-3 px-4 font-medium text-white whitespace-nowrap">Reason</th>
              <th className="py-3 px-4 font-medium text-white whitespace-nowrap">Status</th>
              <th className="py-3 px-4 font-medium text-white whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="py-10 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-10 text-center text-gray-500">
                  No requests
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => (
                <React.Fragment key={row.id}>
                  <tr className="text-sm border-b border-[#E4E4E7] hover:bg-[#F8FAFC]">
                    <td className="py-3 px-4 whitespace-nowrap text-[#667085]">
                      {row.created_at ? dayjs(row.created_at).format("MM-DD-YYYY h:mm A") : "—"}
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-medium text-[#232323] whitespace-nowrap">
                        {row.firstname} {row.lastname}
                      </div>
                      {row.email && (
                        <div className="text-xs text-[#667085]">{row.email}</div>
                      )}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">{row.phone || "—"}</td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      {DEPT_NAMES[row.departmentid] || `Dept ${row.departmentid}`}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      {row.requested_date || "—"}
                      {row.requested_time ? ` · ${row.requested_time}` : ""}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">{row.provider || "—"}</td>
                    <td className="py-3 px-4 max-w-[220px] truncate" title={row.reason}>
                      {row.reason || "—"}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-[3px] rounded-full text-xs font-medium ${
                          (STATUS_META[row.status] || STATUS_META[1]).cls
                        }`}
                      >
                        {(STATUS_META[row.status] || STATUS_META[1]).label}
                      </span>
                      {row.status === 2 && row.handled_by && (
                        <div className="text-[11px] text-[#667085] mt-1">
                          by {row.handled_by}
                          {row.handled_at
                            ? ` · ${dayjs(row.handled_at).format("MM-DD h:mm A")}`
                            : ""}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-[#667085]">
                        {row.status !== 2 && (
                          <button
                            title="Mark handled"
                            disabled={busy}
                            onClick={() => updateStatus(row, 2)}
                            className="hover:text-[#166534]"
                          >
                            <CheckCircleOutlineIcon fontSize="small" />
                          </button>
                        )}
                        {row.status === 1 && (
                          <button
                            title="Needs callback"
                            disabled={busy}
                            onClick={() => updateStatus(row, 3)}
                            className="hover:text-[#B45309]"
                          >
                            <PhoneCallbackIcon fontSize="small" />
                          </button>
                        )}
                        {row.status !== 1 && (
                          <button
                            title="Reopen"
                            disabled={busy}
                            onClick={() => updateStatus(row, 1)}
                            className="hover:text-[#1E40AF]"
                          >
                            <ReplayIcon fontSize="small" />
                          </button>
                        )}
                        {type === "new_patient" && (
                          <button
                            title="Text intake link"
                            disabled={busy}
                            onClick={() => setConfirmSend(row)}
                            className="hover:text-[#1D2951]"
                          >
                            <SendOutlinedIcon fontSize="small" />
                          </button>
                        )}
                        <button
                          title={row.admin_notes ? "Notes (has content)" : "Notes"}
                          disabled={busy}
                          onClick={() => toggleNotes(row)}
                          className={row.admin_notes ? "text-[#E17954]" : "hover:text-[#1D2951]"}
                        >
                          <StickyNote2OutlinedIcon fontSize="small" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {notesOpenId === row.id && (
                    <tr className="border-b border-[#E4E4E7] bg-[#F8FAFC]">
                      <td colSpan={9} className="py-3 px-6">
                        <div className="flex gap-3 items-start">
                          <textarea
                            value={notesDraft}
                            onChange={(e) => setNotesDraft(e.target.value)}
                            rows={3}
                            className="flex-1 border border-[#D5D5D5] rounded-[10px] p-2 text-sm outline-none"
                            placeholder="Internal notes about this request"
                          />
                          <div className="flex flex-col gap-2">
                            <button
                              disabled={busy}
                              onClick={() => saveNotes(row)}
                              className="rounded-[10px] bg-[#1D2951] px-4 py-1.5 text-sm text-white hover:opacity-90"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setNotesOpenId(null)}
                              className="rounded-[10px] border border-[#D5D5D5] px-4 py-1.5 text-sm text-[#1d2951]"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Intake-link confirmation */}
      <Dialog open={!!confirmSend} onClose={() => setConfirmSend(null)}>
        <DialogTitle>Text intake link?</DialogTitle>
        <DialogContent>
          <div className="text-sm text-[#4B5563]">
            Send the{" "}
            <b>{DEPT_NAMES[confirmSend?.departmentid] || "Odessa"}</b> new-patient
            intake link by SMS to{" "}
            <b>
              {confirmSend?.firstname} {confirmSend?.lastname}
            </b>{" "}
            at <b>{confirmSend?.phone}</b>?
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSend(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => sendIntakeLink(confirmSend)}
            style={{ backgroundColor: "#1D2951" }}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={busy} sx={{ zIndex: 2000, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <ToastMessage
        open={openToast}
        handleClose={() => setOpenToast(false)}
        message={toastMessage}
        severity={toastSeverity}
      />
    </div>
  );
};

export default WebRequestsTable;
